import { useState, useEffect, useCallback, useRef } from "react";
import { Batch, SheetData } from "../../../../pages/playground/types";
import useGlobalStore from "../../../../store";
import { getSheetData } from "../../../../api/sheet";
import { AxiosError } from "axios";

export interface UseSheetDataReturn {
  sheet: SheetData | null;
  sheetTotalItems: number;
  fetchSheetData: () => Promise<void>;
  fetchingSheetData: boolean;
}

/**
 * Hook to fetch and manage sheet data with auto-refresh
 *
 * - Fetches sheet data on mount
 * - Handles GDrive login errors
 *
 * @param batchId - The batch ID to fetch sheet data for (can be undefined)
 * @returns Sheet data and fetch function
 */
export function useSheetData(batch: Batch | undefined): UseSheetDataReturn {
  const [fetchingSheetData, setFetchingSheetData] = useState(false);
  const [sheetData, setSheetData] = useState<SheetData | null>(null);
  const sheetIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { setShowGDriveLoginError } = useGlobalStore();

  const fetchSheetData = useCallback(async () => {
    if (batch?.id) {
      try {
        setFetchingSheetData(true);
        const response = await getSheetData(batch.id);

        if (response?.data?.data) {
          setSheetData(response.data.data);
        }
      } catch (error) {
        const response = (error as AxiosError).response;

        if (response?.status !== 404) {
          setShowGDriveLoginError(true);
        }
      } finally {
        setFetchingSheetData(false);
      }
    }
  }, [batch?.id, setShowGDriveLoginError]);

  // Fetch sheet data initially
  useEffect(() => {
    fetchSheetData();
  }, [fetchSheetData]);

  useEffect(() => {
    if (sheetIntervalRef.current) {
      clearInterval(sheetIntervalRef.current);
      sheetIntervalRef.current = null;
    }

    // if (shouldPoll) {
    //   sheetIntervalRef.current = setInterval(() => {
    //     fetchSheetData();
    //   }, SHEET_FETCH_INTERVAL);
    // }

    return () => {
      if (sheetIntervalRef.current) {
        clearInterval(sheetIntervalRef.current);
        sheetIntervalRef.current = null;
      }
    };
  }, [sheetData, fetchSheetData]);

  useEffect(() => {
    return () => {
      if (sheetIntervalRef.current) {
        clearInterval(sheetIntervalRef.current);
        sheetIntervalRef.current = null;
      }
    };
  }, []);

  return {
    sheet: sheetData,
    sheetTotalItems: sheetData?.items?.length || 0,
    fetchSheetData,
    fetchingSheetData,
  };
}
